import { flatMap, forEach, isEmpty, uniq } from "lodash";
import { call, fork, put, select } from "redux-saga/effects";

import { type CreateBookingsResult, createBookings } from "../../../common/api";
import {
  type GoodsValidationError,
  getNewGoodsItem,
  isGoodsEmpty,
} from "../../../common/goods/goodsUtils";
import type { Sailing } from "../../../common/graphql/fragments/gql/Sailing";
import { Errors } from "../../../common/texts";
import type { TrackerCategory } from "../../../common/tracking/trackerService";
import { transformBookingCandidates } from "../../../use-cases/create/utils/bookingCandidateUtils";
import type { CreateBookings_createBookings_results } from "../../../use-cases/multiple-create/graphql/gql/CreateBookings";
import { updateBookingsInGridSaga } from "../../grid/gridSaga";
import { hideCreatePanel } from "../../multi-create/multiCreateActions";
import {
  addBookingResultNotification,
  type NotificationResult,
} from "../../notifications/notificationsActions";
import { createCreatedAt } from "../../notifications/notificationsUtils";
import { showBookingResultToast } from "../../notifications/utils/showToast";
import {
  type CreateFormIdVariant,
  type CreateRequestSubmitAction,
  setBookingsToKeepInCreateForm,
} from "../createFormActions";
import type { CreateCandidate, CreateFormState } from "../createFormReducer";
import {
  finishSubmit,
  setCreateResult,
  startSubmit,
} from "../createMetaActions";
import {
  type CreateValidationErrors,
  getCreateFormState,
  type SpecificCreateValidationErrors,
} from "../createReducer";
import { initializeCreateFormThunk } from "../thunks/initializeCreateFormThunk";
import {
  getBookingsToKeepInForm,
  transformCreateBookingsResult,
} from "../utils/transformCreateBookingResults";
import {
  trackBookingCreateResult,
  trackCreateEvent,
} from "./trackCreateResult";

export function* showBookingResultNotification(
  bookingResult: NotificationResult,
) {
  yield call(showBookingResultToast, bookingResult);
  yield put(addBookingResultNotification(bookingResult, createCreatedAt()));
}

function validateCandidateSpecificFields(
  candidate: CreateCandidate,
): SpecificCreateValidationErrors[number] | null {
  const validationErrors: SpecificCreateValidationErrors[number] = {};

  if (!isGoodsEmpty(candidate.goods)) {
    forEach(candidate.goods, (item) => {
      const validationErrorsForItem: GoodsValidationError =
        validationErrors.goods?.[item.id] ?? {};

      if (!item.goodsType) {
        validationErrorsForItem.type = Errors.SelectGoodsType;
      }
      if (!item.description.trim()) {
        validationErrorsForItem.description = Errors.EnterGoodsDescription;
      }
      if (item.weight === undefined) {
        validationErrorsForItem.weight = Errors.EnterGoodsWeight;
      }

      if (!isEmpty(validationErrorsForItem)) {
        validationErrors.goods = {
          ...validationErrors.goods,
          [item.id]: validationErrorsForItem,
        };
      }
    });
  }

  return validationErrors;
}

function validate(state: CreateFormState): CreateValidationErrors | null {
  const validationErrors: CreateValidationErrors = {
    common: {},
    specific: {},
  };

  if (!state?.sailing) {
    validationErrors.common.sailing = Errors.SelectDepartureTime;
  }

  if (!state?.departureDate) {
    validationErrors.common.departureDate = Errors.SelectDepartureDate;
  }

  state?.bookings.forEach((booking, index) => {
    const errors = validateCandidateSpecificFields(booking);

    if (!isEmpty(errors)) {
      validationErrors.specific[index] ??= errors;
    }
  });

  if (isEmpty(validationErrors.common) && isEmpty(validationErrors.specific)) {
    return null;
  }

  return validationErrors;
}

function* validateSubmitInput(
  formId: CreateFormIdVariant,
  formState: CreateFormState,
  trackerCategory: TrackerCategory,
) {
  const validationErrors = validate(formState);

  if (validationErrors != null) {
    trackCreateEvent(
      trackerCategory,
      "Booking failed (validation error)",
      formState.bookings.length,
    );

    yield showBookingResultNotification({
      type: "create-fail",
      count: formState.bookings.length,
      sailing: formState.sailing,
      warnings: [],
      errors: [Errors.BookingCreationValidationProblem],
    });

    yield put(setCreateResult(formId, {}, validationErrors));

    return false;
  }

  return true;
}

export function* handleRequestSubmit(action: CreateRequestSubmitAction) {
  const formState: CreateFormState = yield select(
    getCreateFormState(action.payload.formId),
  );

  if (!formState) {
    return;
  }

  const trackerCategory: TrackerCategory =
    action.payload.formId === "simple" ? "SimpleCreate" : "MultiCreate";

  const isValid = yield* validateSubmitInput(
    action.payload.formId,
    formState,
    trackerCategory,
  );

  if (!isValid) {
    return;
  }

  yield put(startSubmit(action.payload.formId));
  try {
    const bookings = transformBookingCandidates(formState);
    const { errors, results }: CreateBookingsResult = yield call(
      createBookings,
      bookings,
    );

    const sailing = formState.sailing;

    if (results) {
      trackBookingCreateResult(trackerCategory, results);

      yield call(showBookingResultToasts, results, sailing);
      yield put(setCreateResult(action.payload.formId, { result: results }));

      const bookingsToKeepInForm = getBookingsToKeepInForm(results, formState);

      const allAreSuccess = bookingsToKeepInForm.length === 0;
      const anyAreSuccess = bookingsToKeepInForm.length !== bookings.length;

      if (anyAreSuccess) {
        yield fork(updateBookingsInGridSaga);
      }

      if (allAreSuccess) {
        yield put(hideCreatePanel());
        yield put(
          initializeCreateFormThunk({
            formId: action.payload.formId,
            initialGoodsItem: getNewGoodsItem(),
          }),
        );
      } else {
        yield put(
          setBookingsToKeepInCreateForm(
            action.payload.formId,
            bookingsToKeepInForm,
          ),
        );
      }
    } else {
      trackCreateEvent(
        trackerCategory,
        "Booking failed (request failed)",
        formState.bookings.length,
      );
      yield showBookingResultNotification({
        type: "create-fail",
        count: bookings.length,
        sailing,
        warnings: [],
        errors: [Errors.BookingNotAdded],
      });

      if (errors) {
        yield put(
          setCreateResult(action.payload.formId, { genericError: errors[0] }),
        );
      } else {
        yield put(
          setCreateResult(action.payload.formId, {
            genericError: Errors.RequestFailed,
          }),
        );
      }
    }
  } catch (e) {
    trackCreateEvent(
      trackerCategory,
      "Booking failed (request failed)",
      formState.bookings.length,
    );
    console.error(e);

    yield showBookingResultNotification({
      type: "create-fail",
      count: formState.bookings.length,
      sailing: formState.sailing,
      warnings: [],
      errors: [Errors.RequestFailed],
    });
    yield put(
      setCreateResult(action.payload.formId, {
        genericError: Errors.RequestFailed,
      }),
    );
  } finally {
    yield put(finishSubmit(action.payload.formId));
  }
}

export function* showBookingResultToasts(
  results: CreateBookings_createBookings_results[],
  sailing: Sailing | null,
) {
  const { created, failed, waitlisted } =
    transformCreateBookingsResult(results);

  if (failed.length > 0) {
    yield call(showBookingResultNotification, {
      type: "create-fail",
      count: failed.length,
      errors: uniq(flatMap(failed, (b) => b.errors)),
      warnings: uniq(flatMap(failed, (b) => b.warnings)),
      sailing,
    });
  }

  if (created.length > 0) {
    yield call(showBookingResultNotification, {
      type: "confirmed",
      bookings: created.map((b) => b.booking),
      messages: uniq(flatMap(created, (b) => b.messages)),
      warnings: uniq(flatMap(created, (b) => b.warnings)),
      sailing,
    });
  }

  if (waitlisted.length > 0) {
    yield call(showBookingResultNotification, {
      type: "waitlist",
      bookings: waitlisted.map((b) => b.booking),
      messages: uniq(flatMap(waitlisted, (b) => b.messages)),
      warnings: uniq(flatMap(waitlisted, (b) => b.warnings)),
      sailing,
    });
  }
}
